<template>
  <!--
    .publications = the clipping viewport (overflow: hidden, fills flex parent)
    .feed-track   = the sliding strip (height = n × 100%, moves via translateY)
    .pub-slide    = one publication, exactly 100% of .publications height
  -->
  <div
    class="publications"
    ref="feedRef"
    @touchstart.passive="onTouchStart"
    @touchend.passive="onTouchEnd"
    @mousedown="onMouseDown"
    @mouseup="onMouseUp"
  >
    <!-- Overlays — kept outside the track so they don't slide with it -->
    <Comments style="z-index: 2000;" @publisher-click="onPublisher" ref="comments" />
    <PlaylistChooser style="z-index: 2000;" ref="playlistChooser" />
    <div v-if="DisplayCopied" class="share-view">Link successfully copied</div>

    <!-- The sliding strip -->
    <div class="feed-track" :style="trackStyle">
      <div v-if="publicationList.length === 0 && !loading" class="content-placeholder">
        <div class="placeholder-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 2L2 7l10 5 10-5-10-5z"/>
            <path d="M2 17l10 5 10-5"/>
            <path d="M2 12l10 5 10-5"/>
          </svg>
        </div>
        <h3>Nothing found </h3>
        <p>Your feed will appear here. Sorry we found nothing or internet connection's issue.</p>
        <button class="retry-btn" @click="fetchMore(true)">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="23 4 23 10 17 10"/>
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
          </svg>
          Retry
        </button>
      </div>
      <div
        v-for="(pub, index) in publicationList"
        :key="pub._id || index"
        class="pub-slide"
        :data-index="index"
      >
        <Publication
          :infos="pub"
          :index="index"
          v-if="Math.abs(index - currentVisibleIndex) <= 2"
          @publisher-click="onPublisher(pub.userId)"
          @comment="onComment(pub)"
          @share="onShare(pub, index)"
          @like="liking(pub, index)"
          @played="setAsViwed(pub, index)"
          @addToPlaylist="$refs.playlistChooser.show(pub)"
          @ended="onPublicationEnded(index)"
          ref="publication"
        />
        <div v-else class="pub-slide-placeholder" />
      </div>
    </div>
    <div v-if="loading" class="lazy-load-bar">
      <div class="lazy-load-bar__fill"></div>
    </div>

    <Profile style="z-index: 2001;" :visible="false" ref="profile" />
    <div class="keyboard-hint">
      ↑ ↓ keys to navigate
    </div>
  <!-- <Loader :loading="loading"/> -->
  </div>
</template>

<script>

// import Loader from "@/views/components/loader/Loader.vue"
import Publication from './Publication.vue'
import Comments from './Comments.vue';
import Profile from "@/views/profile/Profile.vue"
import PlaylistChooser from '@/views/components/playlistChooser/PlaylistChooser.vue';
import { isNativeAudio, NativeAudio } from '@/plugin/nativeAudio'

export default {
  components: {
    // Loader,
    Publication,
    Comments,
    Profile,
    PlaylistChooser
  },
  name: 'Publications',

  data () {
    return {
      // lazy loader
      hasMore: true,
      page: 0,

      mounted:false,
      publicationList: [],
      loading: false,
      DisplayCopied: false,

      // ── Swipe state ──────────────────────────────────────────────────────────
      currentVisibleIndex: 0,
      isAnimating: false,

      // touch
      touchStartY: 0,
      touchStartX: 0,

      // mouse (desktop drag)
      mouseStartY: 0,
      mouseDragging: false,
      // In Publications.vue data()
      ghostPrev: null,
      ghostNext: null
    }
  },

  computed: {
    trackStyle () {
      // Shift the strip up by (currentIndex × 100%) of .publications height
      return {
        transform: `translateY(calc(-${this.currentVisibleIndex} * 100%))`,
      }
    },
  },

  methods: {
    handleMediaNext() {
      this.goNext()
    },
    handleMediaPrev() {
      this.goPrev()
    },
    handleNativeTrackChanged(e) {
      if (this.$store.state.currentPlayer === 'audioPlayer') return
      if (!NativeAudio.ownsQueue('feed')) return
      const idx = this.publicationList.findIndex(p => p.audio === e.detail?.id)
      if (idx >= 0 && idx !== this.currentVisibleIndex) this.goToIndex(idx)
    },
    handleMediaLoadMore() {
      if (!this.$store.state.audioPlayerVisibility) {
        this.fetchMore()
      }
    },
    close () {
      if(this.$store.state.audioPlayerVisibility === true) {
        this.$store.commit('setAudioPlayerVisibility',false)
        this._queueSet = false
        this.syncNativePlaylist(true)
        return false
      }
      if(this.$refs.profile.Display ) {
        this.$refs.profile.hide()
        return false
      }
      if(this.$refs.comments.Display ) {
        this.$refs.comments.close()
        return false
      }
      if( this.$refs.playlistChooser.Display) {
        this.$refs.playlistChooser.close()
        return false
      }
      return true
    },
    // ── Swipe: touch ───────────────────────────────────────────────────────────
    onTouchStart (e) {
      if(this.$refs.comments.Display || this.$refs.playlistChooser.Display || this.$refs.profile.Display ) return
      this.touchStartY = e.touches[0].clientY
      this.touchStartX = e.touches[0].clientX
    },
    onTouchEnd (e) {
      if(this.$refs.comments.Display || this.$refs.playlistChooser.Display || this.$refs.profile.Display ) return
      if (this.isAnimating) return
      const dy = this.touchStartY - e.changedTouches[0].clientY   // + = swipe up → next
      const dx = Math.abs(this.touchStartX - e.changedTouches[0].clientX)
      // Ignore tiny or mostly-horizontal gestures
      if (Math.abs(dy) < 40 || dx > Math.abs(dy)) return
      dy > 0 ? this.goNext() : this.goPrev()
    },

    // ── Swipe: mouse (desktop) ─────────────────────────────────────────────────
    onMouseDown (e) {
      if(this.$refs.comments.Display || this.$refs.playlistChooser.Display || this.$refs.profile.Display ) return
      this.mouseStartY   = e.clientY
      this.mouseDragging = true
    },
    onMouseUp (e) {
      if(this.$refs.comments.Display || this.$refs.playlistChooser.Display || this.$refs.profile.Display ) return
      if (!this.mouseDragging || this.isAnimating) return
      this.mouseDragging = false
      const dy = this.mouseStartY - e.clientY
      if (Math.abs(dy) < 40) return
      dy > 0 ? this.goNext() : this.goPrev()
    },
    getPubComponent(index) {
      const pubs = this.$refs.publication
      if (!Array.isArray(pubs)) return null
      return pubs.find(p => p && p.index === index)
    },
    preloadAhead() {
      for (let i = 1; i <= 3; i++) {
        const comp = this.getPubComponent(this.currentVisibleIndex + i)
        if (comp && typeof comp.preload === 'function') comp.preload()
      }
    },
    getGhostFor(audioFilename) {
      if (this.ghostPrev && this.ghostPrev.src.includes(audioFilename)) {
        const g = this.ghostPrev
        this.ghostPrev = null
        return g
      }
      if (this.ghostNext && this.ghostNext.src.includes(audioFilename)) {
        const g = this.ghostNext
        this.ghostNext = null
        return g
      }
      return null
    },
    // Publications.vue methods
    /*getGhostFor(index) {
      if (index === this.currentVisibleIndex - 1) return this.ghostPrev
      if (index === this.currentVisibleIndex + 1) return this.ghostNext
      return null
    },*/
    // Called after every navigation
    prepareGhosts() {
      const base    = this.$store.state.baseUrl
      const prev    = this.publicationList[this.currentVisibleIndex - 1]
      const next    = this.publicationList[this.currentVisibleIndex + 1]

      // native ----------------------
      if (isNativeAudio) {
        // priority: next, previous, next+1. ~160 KB each (~25 s at 48 kbps)
        const items = [1, -1, 2]
          .map(d => this.publicationList[this.currentVisibleIndex + d])
          .filter(p => p?.audio)
          .map(p => ({ id: p.audio, url: base + 'audios/' + p.audio }))
        NativeAudio.preload(items)
      }

      [prev, next].forEach((pub, i) => {
        if (isNativeAudio) return   // skip HTML ghost entirely on Android
        if (!pub) return

        // ── Audio ghost ──────────────────────────────────────────────────
        const audioUrl = base + 'audios/' + pub.audio
        const existing = i === 0 ? this.ghostPrev : this.ghostNext

        if (!isNativeAudio && pub.audio && (!existing || !existing.src.includes(pub.audio))) {
          if (existing) existing.src = ''
          const ghost             = new Audio()
          ghost.crossOrigin       = 'anonymous'
          ghost.preload           = 'auto'
          ghost.src               = audioUrl
          if (i === 0) this.ghostPrev = ghost
          else         this.ghostNext = ghost
        }

        // ── Image ghosts — warms HTTP cache ──────────────────────────────
        [pub.img, pub.imgMini].forEach(imgPath => {
          if (!imgPath) return
          const img = new Image()
          img.src   = base + 'images/' + imgPath
        })
      })
    },
    // ── Navigation ─────────────────────────────────────────────────────────────
    goToIndex (index) {
      if (index < 0 || index >= this.publicationList.length) return
      this.stopIndex(this.currentVisibleIndex)
      this.currentVisibleIndex = index
      this.playIndex(index)
      this.lockAnimation()
      this.checkLoadMore()
    },
    goNext () {
      if (this.currentVisibleIndex < this.publicationList.length - 1) {
        this.goToIndex(this.currentVisibleIndex + 1)
      } else if (this.currentVisibleIndex === this.publicationList.length - 1) {
        this.checkLoadMore()
      }
    },
    goPrev () {
      if (this.currentVisibleIndex > 0) {
        this.goToIndex(this.currentVisibleIndex - 1)
      }
    },
    lockAnimation () {
      this.isAnimating = true
      // Must match the CSS transition duration (400 ms)
      setTimeout(() => { this.isAnimating = false }, 270)
    },

    // ── Keyboard (optional, desktop) ───────────────────────────────────────────
    onKeyDown (e) {
      const tag = document.activeElement?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      if (e.key === 'ArrowDown') this.goNext()
      if (e.key === 'ArrowUp')   this.goPrev()
      if (e.code === 'Space' || e.key === ' ') {
        e.preventDefault()
        const comp = this.getPubComponent(this.currentVisibleIndex)
        if (comp && typeof comp.togglePlay === 'function') comp.togglePlay()
      }
    },

    // ── Play / stop helpers ────────────────────────────────────────────────────
    syncNativeQueue(isInitial = false) {
      if (!isNativeAudio) return
      if (this.$store.state.audioPlayerVisibility) return
      const base = this.$store.state.baseUrl
      const items = this.publicationList
        .filter(p => p?.audio)
        .map(p => ({
          id: p.audio,
          url: base + 'audios/' + p.audio,
          title: p.text || p.title || 'Publication',
          artist: (this.$store.state.users[p.userId] && this.$store.state.users[p.userId].pseudo) || p.author || 'Wedio',
          cover: p.imgMini ? base + 'images/' + p.imgMini : (p.img ? base + 'images/' + p.img : '')
        }))
      if (items.length === 0) return
      const currentPub = this.publicationList[this.currentVisibleIndex]
      const startIndex = items.findIndex(item => item.id === currentPub?.audio)

      if (isInitial || !this._queueSet) {
        NativeAudio.setQueue('feed', items, startIndex >= 0 ? startIndex : 0)
        this._queueSet = true
      } else {
        const newItems = items.slice(this._lastSyncedCount || 0)
        if (newItems.length > 0) {
          NativeAudio.appendQueue(newItems)
        }
      }
      this._lastSyncedCount = items.length
    },
    syncNativePlaylist(isInitial = false) {
      return this.syncNativeQueue(isInitial)
    },
    playIndex(index) {
      this.$store.commit('setCurrentPlayer', 'publication')
      this.$store.commit('player/setCurrentIndex', index)
      const comp = this.getPubComponent(index)
      if (comp && typeof comp.startPlay === 'function') {
        const pub   = this.publicationList[index]
        const ghost = pub?.audio ? this.getGhostFor(pub.audio) : null
        comp.startPlay(ghost)
      }
      this._queueSet = false
      this.syncNativeQueue(true)
      this.$nextTick(() => this.prepareGhosts())
    },
    stopIndex (index) {
      const comp = this.getPubComponent(index)
      if (comp && typeof comp.stopPlay === 'function') comp.stopPlay()
    },

    // ── Auto-advance when a track ends ─────────────────────────────────────────
    onPublicationEnded (index) {
      if (index === this.currentVisibleIndex) this.goNext()
    },

    // ── Jump to a specific index (called by GlobalPlayer) ──────────────────────
/*     goToIndex (index) {
      if (index === this.currentVisibleIndex) return
      this.stopIndex(this.currentVisibleIndex)
      this.currentVisibleIndex = index
      this.playIndex(index)
      this.lockAnimation()
      this.checkLoadMore()
    }, */

    // ── Action handlers (unchanged from original) ──────────────────────────────
    onPublisher (userId) { this.$refs.profile.show({ uid: userId }) },
    onComment (pub)      { this.$refs.comments.show(pub) },

    async onShare (pub, index) {
      const url = this.$store.state.baseUrl + 'publication/' + pub._id
      if (navigator.share) {
        try {
          await navigator.share({ title: 'Link successfully copied', text: 'Link successfully copied', url })
          this.sharePublication(pub, index)
          return { success: true, method: 'share' }
        } catch (error) {
        if (error.name === 'AbortError') return { success: false, method: 'share', cancelled: true }
          console.error('Error sharing:', error)
        }
      }
      try {
        await navigator.clipboard.writeText(url)
        this.DisplayCopied = true
        setTimeout(() => this.DisplayCopied = false, 2000)
        this.sharePublication(pub, index)
        return { success: true, method: 'clipboard' }
      } catch (error) {
        console.error('Failed to copy to clipboard:', error)
        return { success: false, method: 'clipboard', error }
      }
    },

    async sharePublication (pub, index) {
      const comp = this.getPubComponent(index)
      if (comp?.isShared) return
      try {
        await this.$axios.post('/publication.php', { type: 'share', publicationId: pub._id })
        this.publicationList[index].stats = this.publicationList[index].stats || {}
        this.publicationList[index].stats.shares = this.publicationList[index].stats.shares || 0
        this.publicationList[index].stats.shares++
        if (comp) comp.isShared = true
      } catch (e) {
        console.log(e)
      }
    },

    async liking (pub, index) {
      const comp = this.getPubComponent(index)
      const isLiked = comp ? comp.isLiked : false
      const userId  = this.$store.state.user.uid

      // liking before reaching the server for  instant effect
      if (isLiked) {
        this.publicationList[index].likes = this.publicationList[index].likes.filter(lk => lk.userId !== userId)
      } else {
        this.publicationList[index].likes.push({ userId, createdAt: new Date() })
      }
      if (comp) comp.isLiked = !isLiked
      try {

        await this.$axios.post('/publication.php', { type: isLiked ? 'unlike' : 'like', publicationId: pub._id })

      } catch (e) {
        // undoing like/unlike as it failed
        if (isLiked) {
          this.publicationList[index].likes.push({ userId, createdAt: new Date() })
        } 
        else {
          this.publicationList[index].likes = this.publicationList[index].likes.filter(lk => lk.userId !== userId)  
        }
        if (comp) comp.isLiked = isLiked

        console.log(e.response)
      }
    },

    async setAsViwed (pub, index) {
      const userId = this.$store.state.user.uid
      const already = this.publicationList[index].views.find(vw => vw.userId === userId)
      if (already) return
      try {
        const res = await this.$axios.post('/publication.php', { type: 'see', publicationId: pub._id })
        console.log(res.data)
        this.publicationList[index].views.push({ userId, type: 'pub', subjectId: pub._id, createdAt: new Date() })
      } catch (e) {
        console.log(e)
      }
    },
    async fetchMore (isFirstConnection) {
      this.loading = true
      try {
       const res  = await this.$axios.post('publication.php', { type: 'getFeed', page: this.page,isFirstConnection:isFirstConnection ? true : false })
        const pubs = res.data || []
        // console.log(pubs,'_--------------------------')
        if(pubs.length === 0) {
          this.page = 0
          this.fetchMore(true)
          return
        }
        const newPubs = [...this.publicationList, ...pubs]

        this.$store.commit('player/setPublicationList', newPubs)
        
        if (pubs.length <= 0)  return

        // get users publishers metadatas________________________________________________________________
        const usersToGet = new Set()
        pubs.forEach(aud => {
          if(!this.$store.state.users[aud.userId]) usersToGet.add(aud.userId)
        })
        const k = this
        if(usersToGet.size > 0) {
          const res2 = await this.$axios.post('user.php',{type:"getUsersMetaData",users:[...usersToGet]})
          const users = res2.data || []
          users.forEach(us => {
            k.$store.commit('setAnUser',us)
          })
        }
        
        this.publicationList = newPubs
        if (!this.$store.state.audioPlayerVisibility) {
          if (!this._queueSet) {
            this.syncNativeQueue(true)
          } else {
            this.syncNativeQueue(false)
          }
        }
        await this.$nextTick()
        if (!this.mounted)  {
          this.currentVisibleIndex = 0
          this.playIndex(0)
        }
        
        this.page ++
        //---------
        const pub = this.$store.state.player.featuredPub
        this.loadPub(pub)
        this.mounted = true
      } catch (e) {
        console.error(e?.response ?? e)
      } finally {
        this.loading = false
      }
    },

    // ── Load publications ──────────────────────────────────────────────────────
    async search (str ) {
      this.publicationList = []
      this.loading = true
      try {

        const res  = await this.$axios.post('publication.php', { type: 'searchPublications', search: str })
        const pubs = res.data || []
        this.$store.commit('player/setPublicationList', pubs)

        // Reset position and auto-play first item after the list renders
        this.currentVisibleIndex = 0
        await this.$nextTick()
        if (pubs.length > 0) this.playIndex(0)
        else return

        // get users publishers metadatas________________________________________________________________
        const usersToGet = new Set()
        this.publicationList.forEach(aud => {
          if(!this.$store.state.users[aud.userId]) usersToGet.add(aud.userId)
        })
        const k = this
        if(usersToGet.size > 0) {
          const res2 = await this.$axios.post('user.php',{type:"getUsersMetaData",users:[...usersToGet]})
          const users = res2.data || []
          users.forEach(us => {
            k.$store.commit('setAnUser',us)
          })
        }
        
        this.publicationList = pubs
        this._queueSet = false
        if (!this.$store.state.audioPlayerVisibility) {
          this.syncNativeQueue(true)
        }
        //---------
        const pub = this.$store.state.player.featuredPub
        this.loadPub(pub)
        this.mounted = true
      } catch (e) {
        console.error(e?.response ?? e)
      } finally {
        this.loading = false
      }
    },
    loadPub(pub) { // put pub first
      if (!pub) return
      this.$store.commit('player/setFeaturedPub', null)
      this.publicationList = [pub, ...this.publicationList]
      this.$store.commit('player/setPublicationList', this.publicationList)
      this._queueSet = false
      if (!this.$store.state.audioPlayerVisibility) {
        this.syncNativeQueue(true)
      }
      this.$nextTick(() => { this.goToIndex(0) })
    },
    checkLoadMore () {
      if (!this.hasMore || this.loading) return
      const remaining = this.publicationList.length - 1 - this.currentVisibleIndex
      if (remaining <= 2) this.fetchMore() // start fetching 2 slides before the end
    },
  },
  watch: {
    '$store.state.player.featuredPub' ( pub ) { // load pub from link
      if(this.mounted) this.loadPub(pub)
    }
  },
  mounted () {
    this.fetchMore(true)

    // GlobalPlayer "jump to index" event
    this._gpHandler = (e) => this.goToIndex(e.detail.index)
    window.addEventListener('gp:scroll-to', this._gpHandler)

    // Keyboard navigation
    window.addEventListener('keydown', this.onKeyDown)

    // ---- notification player
    this._mediaPrev = () => this.goPrev()
    this._mediaNext = () => this.goNext()
/*     window.addEventListener('media:prev', this._mediaPrev)
    window.addEventListener('media:next', this._mediaNext) */

    window.addEventListener('media:next', this.handleMediaNext)
    window.addEventListener('media:prev', this.handleMediaPrev)
    window.addEventListener('native:track-changed', this.handleNativeTrackChanged)
    window.addEventListener('media:loadmore', this.handleMediaLoadMore)
    this.syncNativePlaylist()
  },

  beforeUnmount () {
    if (this._observer) this._observer.disconnect()
    window.removeEventListener('gp:scroll-to', this._gpHandler)
    window.removeEventListener('keydown', this.onKeyDown)
    // notification player
    window.removeEventListener('media:prev', this._mediaPrev)
    window.removeEventListener('media:next', this._mediaNext)
    window.removeEventListener('media:next', this.handleMediaNext)
    window.removeEventListener('media:prev', this.handleMediaPrev)
    window.removeEventListener('native:track-changed', this.handleNativeTrackChanged)
    window.removeEventListener('media:loadmore', this.handleMediaLoadMore)

    if (this.ghostPrev) this.ghostPrev.src = ''
    if (this.ghostNext) this.ghostNext.src = ''
  },
  unmounted() {
    window.removeEventListener('media:next', this.handleMediaNext)
    window.removeEventListener('media:prev', this.handleMediaPrev)
    window.removeEventListener('media:track', this.handleMediaTrack)
  },
}
</script>

<style scoped>
/*
  .publications  — the clipping viewport.
  It fills .content-area in Home.vue (flex:1 + min-height:0).
  overflow:hidden is the key: it hides the slides above/below the current one.
*/
.publications {
  position: relative;
  width: 100%;
  height: 100%;           /* fills the flex:1 space given by Home.vue */
  overflow: hidden;       /* clips everything outside the visible slide */
  background: #0a0e26;
  user-select: none;      /* prevent accidental text selection while dragging */
  touch-action: pan-x;    /* let the browser handle horizontal pan; we own vertical */
}

/*
  .feed-track  — the full-height strip containing all slides stacked vertically.
  Height = 100% of .publications (per slide), slides stack naturally.
  translateY moves it up/down by exact multiples of .publications height.
*/
.feed-track {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
}

/*
  .pub-slide  — one publication card.
  height:100% = 100% of .feed-track = 100% of .publications.
  So each slide is exactly one "viewport" tall.
*/
.pub-slide {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}

/* "Link copied" toast — stays fixed in the viewport regardless of track position */
.share-view {
  z-index: 1000;
  position: absolute;
  top: calc(50% - 13px);
  left: 50%;
  transform: translateX(-50%);
  width: 190px;
  height: 35px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(10, 14, 38, 0.95);
  border: 1px solid rgba(0, 212, 255, 0.2);
  color: #e0f7ff;
  font-size: 12px;
  font-weight: 500;
  border-radius: 6px;
  white-space: nowrap;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.keyboard-hint {
  display: none;
  position: fixed;
  top: 70px;
  right: 16px;
  z-index: 200;
  padding: 6px 12px;
  border-radius: 8px;
  background: rgba(10, 14, 38, 0.85);
  border: 1px solid rgba(0, 212, 255, 0.25);
  color: rgba(255, 255, 255, 0.5);
  font-size: 15px;
  pointer-events: none;
}

.content-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 60px 20px;
}

.placeholder-icon {
  width: 80px;
  height: 80px;
  background: rgba(0, 212, 255, 0.1);
  border: 2px solid rgba(0, 212, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  color: #00d4ff;
}

.placeholder-icon svg {
  width: 36px;
  height: 36px;
}

.content-placeholder h3 {
  color: #e0f7ff;
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 12px 0;
}

.content-placeholder p {
  color: #94a3b8;
  font-size: 15px;
  max-width: 500px;
  line-height: 1.6;
  margin: 0;
}
.retry-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
  padding: 10px 20px;
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 10px;
  color: #00d4ff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.retry-btn svg {
  width: 16px;
  height: 16px;
}

.retry-btn:hover {
  background: rgba(0, 212, 255, 0.18);
  border-color: rgba(0, 212, 255, 0.5);
}

.retry-btn:active {
  transform: scale(0.97);
}
.lazy-load-bar {
  position: absolute;
  left: 50%;
  bottom: 120px;
  transform: translateX(-50%);
  width: 96px;
  height: 3px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.15);
  overflow: hidden;
  z-index: 500;
  pointer-events: none;
}

.lazy-load-bar__fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 36%;
  border-radius: 999px;
  background: linear-gradient(90deg, #00d4ff, #8338ec);
  animation: lazy-load-bar-slide 1.1s ease-in-out infinite;
}
.pub-slide-placeholder { width: 100%; height: 100%; }

@keyframes lazy-load-bar-slide {
  0%   { left: -36%; }
  100% { left: 100%; }
}


@media (min-width: 768px) {
  .keyboard-hint { display: block; }
}
</style>